import type {
  PublicFormApiErrorResponse,
  PublicFormApiResponse,
  InstitutionListQuery,
  InstitutionListResponse,
  SubjectSearchQuery,
  SubjectSearchResponse,
  SubmissionFileDeleteRequest,
  SubmissionFileUploadRequest,
  SubmissionFileUploadResponse,
  StudentAdLoginRequest,
  StudentAdLoginResponse,
  StudentEditSubmissionRequest,
  StudentEditSubmissionResponse,
  StudentFormLoadResponse,
  StudentFormSubmitRequest,
  StudentFormSubmitResponse,
} from '../../models/current-student-advanced-standing-form/api'
import { studentFormApiEndpoints } from '../../constants/current-student-advanced-standing-form/apiEndpoints'

export {
  studentFormApiEndpoints,
  type StudentFormApiEndpoint,
  type StudentFormApiEndpointKey,
} from '../../constants/current-student-advanced-standing-form/apiEndpoints'

export class StudentFormApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message)
    this.name = 'StudentFormApiError'
  }
}

export async function loadStudentForm(): Promise<StudentFormLoadResponse> {
  return postJsonWithoutBody<StudentFormLoadResponse>(studentFormApiEndpoints.formLoad)
}

export async function loginStudentToForm(payload: StudentAdLoginRequest): Promise<StudentAdLoginResponse> {
  const body = new URLSearchParams()
  body.set('username', payload.username)
  body.set('password', payload.password)
  body.set('reference', payload.reference)
  body.set('AdmissionsToken1', payload.AdmissionsToken1)

  return requestJson<StudentAdLoginResponse>(studentFormApiEndpoints.adLogin, {
    method: 'POST',
    headers: {
      ...getCsrfHeaders(),
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body,
  })
}

export async function editStudentSubmission(
  payload: StudentEditSubmissionRequest,
): Promise<StudentEditSubmissionResponse> {
  return requestJson<StudentEditSubmissionResponse>(studentFormApiEndpoints.editSubmission, {
    method: 'POST',
    headers: {
      ...getCsrfHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
}

export async function submitStudentForm(payload: StudentFormSubmitRequest): Promise<StudentFormSubmitResponse> {
  return requestJson<StudentFormSubmitResponse>(studentFormApiEndpoints.submitApplication, {
    method: 'POST',
    headers: {
      ...getCsrfHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })
}

export async function searchInstitutions(query: InstitutionListQuery): Promise<InstitutionListResponse> {
  const searchParams = new URLSearchParams()

  if (query.show_empty_institutions != null) {
    searchParams.set('show_empty_institutions', String(query.show_empty_institutions))
  }
  if (query.search != null) {
    searchParams.set('search', query.search)
  }
  if (query.limit != null) {
    searchParams.set('limit', String(query.limit))
  }

  return requestJson<InstitutionListResponse>(`${studentFormApiEndpoints.institutionList}?${searchParams.toString()}`, {
    method: 'GET',
    headers: getCsrfHeaders(),
  })
}

export async function searchSubjects(query: SubjectSearchQuery): Promise<SubjectSearchResponse> {
  const searchParams = new URLSearchParams()
  searchParams.set('search', query.search)
  searchParams.set('basic_as', String(query.basic_as))

  if (query.limit != null) {
    searchParams.set('limit', String(query.limit))
  }

  return requestJson<SubjectSearchResponse>(`${studentFormApiEndpoints.subjectSearch}?${searchParams.toString()}`, {
    method: 'GET',
    headers: getCsrfHeaders(),
  })
}

export async function uploadSubmissionFile(
  file: File,
  metadata: Required<Pick<SubmissionFileUploadRequest, 'qquuid' | 'qqfilename' | 'qqtotalfilesize'>>,
): Promise<SubmissionFileUploadResponse> {
  const body = new FormData()
  body.append('qquuid', metadata.qquuid)
  body.append('qqfilename', metadata.qqfilename)
  body.append('qqtotalfilesize', String(metadata.qqtotalfilesize))
  body.append('qqfile', file, metadata.qqfilename)

  return requestJson<SubmissionFileUploadResponse>(studentFormApiEndpoints.fileUpload, {
    method: 'POST',
    headers: getCsrfHeaders(),
    body,
  })
}

export async function deleteSubmissionFile(
  payload: Required<Pick<SubmissionFileDeleteRequest, 'qquuid'>>,
): Promise<SubmissionFileUploadResponse> {
  const body = new URLSearchParams()
  body.set('qquuid', payload.qquuid)
  body.set('_method', 'DELETE')

  return requestJson<SubmissionFileUploadResponse>(studentFormApiEndpoints.fileUpload, {
    method: 'POST',
    headers: {
      ...getCsrfHeaders(),
      'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
    },
    body,
  })
}

function postJsonWithoutBody<TResponse extends PublicFormApiResponse<unknown>>(url: string): Promise<TResponse> {
  return requestJson<TResponse>(url, {
    method: 'POST',
    headers: getCsrfHeaders(),
  })
}

async function requestJson<TResponse>(url: string, init: RequestInit): Promise<TResponse> {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...init,
  })

  if (!response.ok) {
    throw new StudentFormApiError(await readErrorMessage(response), response.status)
  }

  return response.json() as Promise<TResponse>
}

async function readErrorMessage(response: Response): Promise<string> {
  const responseText = await response.text()
  if (!responseText) return response.statusText

  try {
    const data = JSON.parse(responseText) as Partial<PublicFormApiErrorResponse>
    return data.error?.message ?? responseText
  } catch {
    return responseText
  }
}

export function createStudentAdLoginRequest(username: string, password: string): StudentAdLoginRequest {
  return {
    username,
    password,
    reference: getParameterByName('reference') ?? '',
    AdmissionsToken1: getCookie('AdmissionsToken1') ?? '',
  }
}

function getCsrfHeaders(): Record<string, string> {
  let token = getCookie('CSRF-TOKEN')
  if (token == null || token === '') {
    token = randomCryptoString()
    document.cookie = `CSRF-TOKEN=${token}; path=/ ; SameSite=Strict`
  }

  return {
    'X-CSRF-TOKEN': token,
  }
}

function getCookie(name: string): string | undefined {
  const cookies = document.cookie.split('; ')
  const cookieMap: Record<string, string> = {}

  for (let i = cookies.length - 1; i >= 0; i -= 1) {
    const cookie = cookies[i]
    if (!cookie) continue

    const [cookieName, ...valueParts] = cookie.split('=')
    if (cookieName === undefined) continue

    cookieMap[cookieName] = valueParts.join('=')
  }

  return cookieMap[name]
}

function getParameterByName(name: string): string | null {
  const normalizedName = name.replace(/[[\]]/g, '\\$&')
  const regex = new RegExp(`[?&]${normalizedName}(=([^&#]*)|&|#|$)`)
  const results = regex.exec(window.location.href)

  if (!results) return null
  if (!results[2]) return ''

  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

function randomCryptoString(length = 32): string {
  const bytes = new Uint8Array(length)
  window.crypto.getRandomValues(bytes)

  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
}
